import React, { FC, useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { useToast } from '@common/toast';
import ConfirmModal from '@components/accountLinking/modals/confirmModal';
import CompleteModal from '@components/accountLinking/modals/completeModal';
import CancelModal from '@components/accountLinking/modals/cancelModal';
import AlreadySupportedModal from '@components/accountLinking/modals/alreadySupportedModal';
import AlreadyLinkedMsgModal from '@components/accountLinking/modals/alreadyLinkedMsgModal';
import UnLinkableModal from '@components/accountLinking/modals/unLinkableModal';
import FailureModal from '@components/accountLinking/modals/failureModal';
import ReconfirmModal from '@components/accountLinking/modals/reconfirmModal';
import { useCheckWritingResume, useApplicantsAccountLink } from '@repository/match';
import useDebounce from '@utils/hooks/useDebounce';

export type ModalType =
  | 'LINK_COMPLETE'
  | 'LINK_CONFIRM'
  | 'RE_LINK_CONFIRM'
  | 'LINK_CANCEL'
  | 'LINK_FAIL'
  | 'LINK_RECONFIRM'
  | 'NOT_LINKABLE'
  | 'ALREADY_SUPPORTED'
  | 'ALREADY_LINKED_MSG'
  | null;

interface JDModalsProps {
  positionId: number;
  publicKey: string;
}

const AccountLinkingModals: FC<JDModalsProps> = ({ positionId, publicKey }) => {
  const Toast = useToast();
  const [modalState, setModalState] = useState<ModalType>(null);

  const { data: checkData, isFetched: isFetchedCheckWritingResume } = useCheckWritingResume({
    positionId,
    publicKey,
  });
  const { mutateAsync: accountLinkMutateAsync } = useApplicantsAccountLink(
    () => {
      setModalState('LINK_COMPLETE');
      return;
    },
    (err: AxiosError) => {
      switch (err?.response?.data?.errorCode) {
        case 'B902': //종료 또는 삭제된 포지션 입니다.
          setModalState('LINK_FAIL');
          return;
        case 'B903': //  이미 연동된 계정입니다.
          setModalState('ALREADY_LINKED_MSG');
          return;
        case 'B904': //이미 지원한 포지션 입니다.
          setModalState('ALREADY_SUPPORTED');
          return;
        case 'B905': // 연동할 수 없는 계정입니다.
          setModalState('NOT_LINKABLE');
          return;
        case 'B901': //ATS 서버와 통신에 실패했습니다.
        default:
          Toast({ type: 'error', iconType: 'info', content: err?.response?.data?.message });
          return;
      }
    },
  );

  const isVisible = (modalType: ModalType) => modalType === modalState;
  const handleClose = () => setModalState(null);

  const onLinkAccount = useDebounce(async () => {
    await accountLinkMutateAsync({
      positionId,
      publicKey,
    });
  }, 1000);

  const onCancelAccountLink = () => {
    setModalState('LINK_CANCEL');
  };

  useEffect(() => {
    if (checkData) {
      if (checkData.resumeWritingYn) setModalState('RE_LINK_CONFIRM');
      else setModalState('LINK_CONFIRM');
    }
  }, [checkData]);

  if (!isFetchedCheckWritingResume) return null;

  return (
    <>
      {/*아이디 연동 진행 모달*/}
      <ConfirmModal visible={isVisible('LINK_CONFIRM')} onConfirm={onLinkAccount} onCancel={onCancelAccountLink} />
      {/*작성 중 지원서 있는 경우 아이디 연동 지원 모달 */}
      <ReconfirmModal visible={isVisible('RE_LINK_CONFIRM')} onConfirm={onLinkAccount} onCancel={onCancelAccountLink} />
      {/*연동 완료 모달*/}
      <CompleteModal visible={isVisible('LINK_COMPLETE')} onClose={handleClose} />
      {/*연동 취소 모달*/}
      <CancelModal visible={isVisible('LINK_CANCEL')} onClose={handleClose} />
      {/*연동 실패 모달*/}
      <FailureModal visible={isVisible('LINK_FAIL')} onClose={handleClose} />
      {/*이미 지원한 이력이 있음 모달*/}
      <AlreadySupportedModal visible={isVisible('ALREADY_SUPPORTED')} onClose={handleClose} />
      {/*이미 연동된 메세지 일 경우 모달*/}
      <AlreadyLinkedMsgModal visible={isVisible('ALREADY_LINKED_MSG')} onClose={handleClose} />
      {/*연동할 수 없는 계정 모달*/}
      <UnLinkableModal visible={isVisible('NOT_LINKABLE')} onClose={handleClose} />
    </>
  );
};

export default AccountLinkingModals;
